import React, { memo, useCallback, useState } from 'react';
import Carousel from 'react-material-ui-carousel';
import { Box, Dialog, IconButton } from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CloseIcon from '@mui/icons-material/Close';
import type { CarouselImage } from './productData';
import './CSS/ImageCarousel.css';

export interface ImageCarouselProps {
  images: CarouselImage[];
  /** Optional aria-label for the carousel region */
  ariaLabel?: string;
  /** Aspect ratio for the slide area e.g. '4/3' or '16/9' */
  aspectRatio?: string;
  /** Disable swipe on touch devices if needed */
  swipe?: boolean;
}

const SlideItem = memo(function SlideItem({
  src,
  alt,
  onImageClick,
}: {
  src: string;
  alt: string;
  onImageClick?: () => void;
}) {
  return (
    <Box
      component="figure"
      onClick={onImageClick}
      sx={{
        margin: 0,
        width: '100%',
        height: '100%',
        minHeight: 240,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'hidden',
        backgroundColor: '#1b1b1b',
        cursor: onImageClick ? 'pointer' : 'default',
      }}
    >
      <Box
        component="img"
        src={src}
        alt={alt}
        loading="lazy"
        decoding="async"
        onError={(e) => {
          (e.target as HTMLImageElement).style.display = 'none';
        }}
        sx={{
          width: '100%',
          height: '100%',
          minHeight: 240,
          objectFit: 'cover',
          objectPosition: 'center',
          display: 'block',
        }}
      />
    </Box>
  );
});

function ImageCarousel({
  images,
  ariaLabel = 'Product images',
  aspectRatio = '4/3',
  swipe = true,
}: ImageCarouselProps) {
  const [enlargedIndex, setEnlargedIndex] = useState<number | null>(null);
  const navWrapperClass = 'ImageCarousel-navWrapper';

  const openEnlarged = useCallback((index: number) => setEnlargedIndex(index), []);
  const closeEnlarged = useCallback(() => setEnlargedIndex(null), []);

  const renderItem = useCallback(
    (item: CarouselImage, index: number) => (
      <SlideItem
        key={`${item.src}-${index}`}
        src={item.src}
        alt={item.alt}
        onImageClick={() => openEnlarged(index)}
      />
    ),
    [openEnlarged]
  );

  const selectedImage = enlargedIndex !== null ? images[enlargedIndex] : null;

  if (!images.length) return null;
  if (images.length === 1) {
    return (
      <>
        <Box
          className="ImageCarousel-root ImageCarousel-single"
          sx={{ width: '100%', height: '100%', minHeight: 240, overflow: 'hidden' }}
          aria-label={ariaLabel}
        >
          <SlideItem
            src={images[0].src}
            alt={images[0].alt}
            onImageClick={() => openEnlarged(0)}
          />
        </Box>
        <Dialog
          open={enlargedIndex === 0}
          onClose={closeEnlarged}
          maxWidth={false}
          PaperProps={{
            sx: {
              backgroundColor: 'transparent',
              boxShadow: 'none',
              maxHeight: '90vh',
              maxWidth: '90vw',
            },
          }}
          slotProps={{ backdrop: { sx: { backgroundColor: 'rgba(0,0,0,0.85)' } } }}
        >
          <IconButton
            aria-label="Close enlarged image"
            onClick={closeEnlarged}
            sx={{
              position: 'absolute',
              top: 8,
              right: 8,
              color: '#fff',
              backgroundColor: 'rgba(0,0,0,0.5)',
              zIndex: 1,
              '&:hover': { backgroundColor: 'rgba(197, 164, 126, 0.9)', color: '#1b1b1b' },
            }}
          >
            <CloseIcon />
          </IconButton>
          {selectedImage && (
            <Box
              component="img"
              src={selectedImage.src}
              alt={selectedImage.alt}
              sx={{ maxWidth: '90vw', maxHeight: '90vh', objectFit: 'contain' }}
            />
          )}
        </Dialog>
      </>
    );
  }

  return (
    <>
      <Box
        className="ImageCarousel-root"
        sx={{
          position: 'relative',
          overflow: 'hidden',
          width: '100%',
          height: '100%',
          minHeight: 240,
        }}
        role="region"
        aria-label={ariaLabel}
        aria-roledescription="carousel"
      >
        <Carousel
          height="100%"
          sx={{ height: '100%' }}
          autoPlay={false}
          animation="fade"
          duration={400}
          swipe={swipe}
          cycleNavigation
          navButtonsAlwaysVisible
          fullHeightHover
          navButtonsWrapperProps={{
            className: navWrapperClass,
            style: { height: '100%', top: 0, display: 'flex', alignItems: 'center' },
          }}
          NavButton={({ onClick, next, prev }) => (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: next ? 'flex-end' : 'flex-start',
                height: '100%',
                px: { xs: 1, sm: 2 },
                zIndex: 2,
              }}
            >
              <IconButton
                onClick={(e: React.MouseEvent<HTMLButtonElement>) => onClick?.(e)}
                aria-label={next ? 'Next image' : 'Previous image'}
                size="medium"
                sx={{
                  backgroundColor: 'rgba(27, 27, 27, 0.85)',
                  border: '1px solid #ffffff',
                  color: '#ffffff',
                  '&:hover': {
                    backgroundColor: 'rgba(197, 164, 126, 0.9)',
                    color: '#1b1b1b',
                  },
                }}
              >
                {next ? <ChevronRightIcon /> : <ChevronLeftIcon />}
              </IconButton>
            </Box>
          )}
          indicators={images.length > 1}
          indicatorContainerProps={{
            className: 'ImageCarousel-indicators',
          }}
          indicatorIconButtonProps={{
            className: 'ImageCarousel-indicatorButton',
          }}
          activeIndicatorIconButtonProps={{
            className: 'ImageCarousel-indicatorButton--active',
          }}
        >
          {images.map((item, index) => renderItem(item, index))}
        </Carousel>
      </Box>
      <Dialog
        open={enlargedIndex !== null}
        onClose={closeEnlarged}
        maxWidth={false}
        PaperProps={{
          sx: {
            backgroundColor: 'transparent',
            boxShadow: 'none',
            maxHeight: '90vh',
            maxWidth: '90vw',
          },
        }}
        slotProps={{ backdrop: { sx: { backgroundColor: 'rgba(0,0,0,0.85)' } } }}
      >
        <IconButton
          aria-label="Close enlarged image"
          onClick={closeEnlarged}
          sx={{
            position: 'absolute',
            top: 8,
            right: 8,
            color: '#fff',
            backgroundColor: 'rgba(0,0,0,0.5)',
            zIndex: 1,
            '&:hover': { backgroundColor: 'rgba(197, 164, 126, 0.9)', color: '#1b1b1b' },
          }}
        >
          <CloseIcon />
        </IconButton>
        {selectedImage && (
          <Box
            component="img"
            src={selectedImage.src}
            alt={selectedImage.alt}
            sx={{ maxWidth: '90vw', maxHeight: '90vh', objectFit: 'contain' }}
          />
        )}
      </Dialog>
    </>
  );
}

export default memo(ImageCarousel);
